import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { CurrencyCode } from "models/enums/currency";
import { RutRotSummaryProps } from "models/propTypes/invoicePropTypes";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { useInvoiceService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";

const RutRotSummary: React.FC<RutRotSummaryProps> = (props) => {
  const invoiceService = useInvoiceService();
  const { values } = props;
  const [houseWorkTypeText, setHouseWorkTypeText] = React.useState<string>(
    values.rutRot?.houseWorkType ? invoiceService.getRutOrRotText(Number(values.rutRot?.houseWorkType)) : ""
  );
  React.useEffect(() => {
    values.rutRot?.houseWorkType &&
      setHouseWorkTypeText(invoiceService.getRutOrRotText(Number(values.rutRot?.houseWorkType)));
  }, [values.rutRot?.houseWorkType]);

  const getTotalLaborCost = () => {
    return (
      values.products?.filter((prod) =>
        // eslint-disable-next-line eqeqeq --- since checked against different types
        values.rutRot?.rutRotArticles?.some((rutRotItem) => rutRotItem.id == prod.uniqueId)
      ) ?? []
    )
      .map((prod) => invoiceService.calculateAmount(prod.price ?? 0, prod.quantity))
      .reduce((a, b) => +a + +b, 0);
  };

  const getTotalMaterialCost = () => {
    return values.rutRot?.rutRotArticles?.map((prod) => prod.materialCost ?? 0).reduce((a, b) => +a + +b, 0) ?? 0;
  };

  const bottomSummaryRows = [
    {
      name: TranslationKeys.Invoice.TotalLaborCost,
      value: getTotalLaborCost(),
    },
    {
      name: TranslationKeys.Invoice.TotalMaterialCost,
      value: getTotalMaterialCost(),
    },
    {
      name: TranslationKeys.Invoice.OtherExpenses,
      value: values.rutRot?.houseWorkOtherCosts,
    },
    {
      name: TranslationKeys.Invoice.TotalAmount,
      value: getTotalLaborCost() + getTotalMaterialCost() + Number(values.rutRot?.houseWorkOtherCosts),
    },
  ];
  return (
    <Grid container spacing={2} direction={"column"}>
      <Grid container item spacing={3} className={cssExports.formLabel}>
        <Grid item container xs={6}>
          {values.rutRot?.houseWorkType &&
            LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.RutRotSummaryService, "", {
              houseWorkType: houseWorkTypeText,
            })}
        </Grid>
        <Grid item container xs={3} justifyContent={"flex-end"}>
          {values.rutRot?.houseWorkType &&
            LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.RutRotSummaryCost, "", {
              houseWorkType: houseWorkTypeText,
            })}
        </Grid>
        <Grid item container xs={3} justifyContent={"flex-end"}>
          {values.rutRot?.houseWorkType &&
            LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.MaterialCost)}
        </Grid>
      </Grid>
      <Grid container item>
        {values.rutRot?.rutRotArticles?.map((item) => {
          // eslint-disable-next-line eqeqeq --- since checked against different types
          const prod = values.products?.find((prod) => prod.uniqueId == item.id);
          return (
            <Grid key={item.id} container item spacing={3}>
              <Grid item xs={6} className={clsx(cssExports.formControlLabel)}>
                {item.name}
              </Grid>
              <Grid
                item
                container
                xs={3}
                justifyContent={"flex-end"}
                className={clsx(cssExports.formValue, cssExports.whiteSpaceNoWrap)}
              >
                {LocalizationHelperInstance.getFormattedCurrency(
                  invoiceService.calculateAmount(prod?.price ?? 0, prod?.quantity),
                  CurrencyCode.SEK,
                  2
                )}
              </Grid>
              <Grid
                item
                container
                xs={3}
                justifyContent={"flex-end"}
                className={clsx(cssExports.formValue, cssExports.whiteSpaceNoWrap)}
              >
                {LocalizationHelperInstance.getFormattedCurrency(item.materialCost ?? 0, CurrencyCode.SEK, 2)}
              </Grid>
            </Grid>
          );
        })}
      </Grid>
      <Grid container item>
        {bottomSummaryRows.map((row, index) => (
          <Grid key={index} container item spacing={3}>
            <Grid item xs={6} className={clsx(cssExports.formControlLabel)}>
              {LocalizationHelperInstance.getFormattedMessage(row.name)}
            </Grid>
            <Grid item container xs={3} justifyContent={"flex-end"} className={cssExports.formValue}>
              {LocalizationHelperInstance.getFormattedCurrency(row.value ?? 0, CurrencyCode.SEK, 2)}
            </Grid>
            <Grid item xs={3}></Grid>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};

export default RutRotSummary;
